import { X, FileImage, Loader2, Printer } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { generateDispatchDocument, generateRentalDocument } from '@/api/orders'
import type { Order } from '@/api/orders'

interface DocumentModalProps {
  isOpen: boolean
  onClose: () => void
  order: Order | null
  type: 'dispatch' | 'rental'
  /** true 時派車單金額一律顯示（不受付款方式/隱藏金額設定影響），給客戶看的版本用 */
  forceAmount?: boolean
}

export default function DocumentModal({ isOpen, onClose, order, type, forceAmount = false }: DocumentModalProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen && order?.id) {
      loadDocument()
    }
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl)
    }
  }, [isOpen, order?.id, type, forceAmount])

  const loadDocument = async () => {
    if (!order?.id) return
    setLoading(true)
    setError(null)
    setImageUrl(null)
    try {
      const blob = type === 'dispatch'
        ? await generateDispatchDocument(order.id, forceAmount)
        : await generateRentalDocument(order.id)
      setImageUrl(URL.createObjectURL(blob))
    } catch (err: any) {
      const msg = err?.response?.data?.detail || '無法載入文件，請確認租戶已配置文檔模板'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    if (!imageUrl) return
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8" />
<style>* { margin:0; padding:0; } body { background:#fff; } img { width:100%; display:block; }
@media print { @page { margin: 0; } }</style>
</head><body><img src="${imageUrl}" /></body></html>`)
    win.document.close()
    win.focus()
    setTimeout(() => win.print(), 500)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative z-10 w-full max-w-4xl max-h-[92vh] mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileImage className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-base font-bold text-gray-900 dark:text-white">
              {type === 'dispatch' ? '派車簽單' : '汽車出租單'}{forceAmount ? '（客戶版）' : ''} — {order?.order_number}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {imageUrl && (
              <Button onClick={handlePrint} className="h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-sm gap-1">
                <Printer className="w-4 h-4" />列印
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onClose} className="cursor-pointer h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100 dark:bg-gray-900 flex items-start justify-center">
          {loading && (
            <div className="flex items-center justify-center h-48 gap-3 text-gray-500">
              <Loader2 className="w-7 h-7 animate-spin text-blue-600" />
              <span>正在生成文件...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-red-500">
              <FileImage className="w-10 h-10" />
              <span className="text-sm text-center max-w-xs">{error}</span>
            </div>
          )}

          {imageUrl && !loading && (
            <img
              src={imageUrl}
              alt={type === 'dispatch' ? '派車單' : '出租單'}
              className="w-full max-w-3xl h-auto rounded-lg shadow-lg"
            />
          )}
        </div>
      </div>
    </div>
  )
}
